import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { ProtectedRoute } from '../guards/ProtectedRoute';
import { MainLayout } from '@/layouts/MainLayout/MainLayout';
import { AuthLayout } from '@/layouts/AuthLayout/AuthLayout';

// Page Imports
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { PipelinePage } from '@/features/pipeline/pages/PipelinePage';
import { PipelineDetailPage } from '@/features/pipeline/pages/PipelineDetailPage';
import { RequestsListPage } from '@/features/requests/pages/RequestsListPage';
import { RequestDetailsPage } from '@/features/requests/pages/RequestDetailsPage';
import { NewRequestPage } from '@/features/requests/pages/NewRequestPage';
import { NewServiceRequestPage } from '@/features/requests/pages/NewServiceRequestPage';
import { EditRequestPage } from '@/features/requests/pages/EditRequestPage';
import { ApprovalsListPage } from '@/features/approvals/pages/ApprovalsListPage';
import { ApprovalDetailsPage } from '@/features/approvals/pages/ApprovalDetailsPage';
import { NfaListPage } from '@/features/nfa/pages/NfaListPage';
import { NfaDetailsPage } from '@/features/nfa/pages/NfaDetailsPage';
import { NfaEditPage } from '@/features/nfa/pages/NfaEditPage';
import { LfaListPage } from '@/features/lfa/pages/LfaListPage';
import { LfaDetailsPage } from '@/features/lfa/pages/LfaDetailsPage';
import { LfaEditPage } from '@/features/lfa/pages/LfaEditPage';
import { PrListPage } from '@/features/pr/pages/PrListPage';
import { PrDetailPage } from '@/features/pr/pages/PrDetailPage';
import { PrCreatePage } from '@/features/pr/pages/PrCreatePage';
import { RfqListPage } from '@/features/rfq/pages/RfqListPage';
import { RfqDetailPage } from '@/features/rfq/pages/RfqDetailPage';
import { RfqCreatePage } from '@/features/rfq/pages/RfqCreatePage';
import { RfqEditPage } from '@/features/rfq/pages/RfqEditPage';
import { VendorsListPage } from '@/features/vendors/pages/VendorsListPage';
import { VendorDetailPage } from '@/features/vendors/pages/VendorDetailPage';
import { VendorCreatePage } from '@/features/vendors/pages/VendorCreatePage';
import { VendorEditPage } from '@/features/vendors/pages/VendorEditPage';
import { QuotationsListPage } from '@/features/quotations/pages/QuotationsListPage';
import { QuotationComparisonPage } from '@/features/quotations/pages/QuotationComparisonPage';
import { PurchaseOrdersListPage } from '@/features/purchase-orders/pages/PurchaseOrdersListPage';
import { PurchaseOrderCreatePage } from '@/features/purchase-orders/pages/PurchaseOrderCreatePage';
import { PurchaseOrderDetailPage } from '@/features/purchase-orders/pages/PurchaseOrderDetailPage';
import { PurchaseOrderEditPage } from '@/features/purchase-orders/pages/PurchaseOrderEditPage';
import { GrnListPage } from '@/features/grn/pages/GrnListPage';
import { GrnCreatePage } from '@/features/grn/pages/GrnCreatePage';
import { GrnDetailPage } from '@/features/grn/pages/GrnDetailPage';
import { GrnEditPage } from '@/features/grn/pages/GrnEditPage';
import { DeliveryTrackingPage } from '@/features/grn/pages/DeliveryTrackingPage';
import { InvoicesListPage } from '@/features/invoices/pages/InvoicesListPage';
import { InvoiceDetailPage } from '@/features/invoices/pages/InvoiceDetailPage';
import { InvoiceEditPage } from '@/features/invoices/pages/InvoiceEditPage';
import { PaymentsListPage } from '@/features/payments/pages/PaymentsListPage';
import { PaymentDetailPage } from '@/features/payments/pages/PaymentDetailPage';
import { PaymentEditPage } from '@/features/payments/pages/PaymentEditPage';
import { AnalyticsPage } from '@/features/analytics/pages/AnalyticsPage';
import { AiPage } from '@/features/ai/pages/AiPage';
import { AuditListPage } from '@/features/audit/pages/AuditListPage';
import { IntegrationLogsPage } from '@/features/integration-logs/pages/IntegrationLogsPage';
import { AdminUsersPage } from '@/features/admin/pages/AdminUsersPage';
import { SapDashboardPage } from '@/features/sap/pages/SapDashboardPage';
import { SapMaterialsPage } from '@/features/sap/pages/SapMaterialsPage';
import { SapVendorsPage } from '@/features/sap/pages/SapVendorsPage';
import { SapMastersPage } from '@/features/sap/pages/SapMastersPage';
import { SapPrPage } from '@/features/sap/pages/SapPrPage';
import { SapPoPage } from '@/features/sap/pages/SapPoPage';
import { SapGrnPage } from '@/features/sap/pages/SapGrnPage';
import { SapInvoicesPage } from '@/features/sap/pages/SapInvoicesPage';
import { SapPaymentsPage } from '@/features/sap/pages/SapPaymentsPage';
import { SapLogsPage } from '@/features/sap/pages/SapLogsPage';
import { SapSimulationControlPage } from '@/features/sap/pages/SapSimulationControlPage';
import { ForbiddenPage } from '../pages/ForbiddenPage';
import { NotFoundPage } from '../pages/NotFoundPage';

import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { MfaPage } from '@/features/auth/pages/MfaPage';

export const AppRouter: React.FC = () => {
  return (
    <Routes>
      {/* Public Auth Routes */}
      <Route
        path={ROUTES.AUTH.LOGIN}
        element={
          <AuthLayout>
            <LoginPage />
          </AuthLayout>
        }
      />
      <Route
        path={ROUTES.AUTH.FORGOT_PASSWORD}
        element={
          <AuthLayout>
            <ForgotPasswordPage />
          </AuthLayout>
        }
      />
      <Route
        path={ROUTES.AUTH.RESET_PASSWORD}
        element={
          <AuthLayout>
            <ResetPasswordPage />
          </AuthLayout>
        }
      />
      <Route
        path={ROUTES.AUTH.MFA}
        element={
          <AuthLayout>
            <MfaPage />
          </AuthLayout>
        }
      />

      {/* Protected Main Routes */}
      <Route
        path={ROUTES.DASHBOARD}
        element={
          <ProtectedRoute>
            <MainLayout>
              <DashboardPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PIPELINE.LIST}
        element={
          <ProtectedRoute requiredPermission="REQUEST_VIEW">
            <MainLayout>
              <PipelinePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PIPELINE.DETAILS(':requestId')}
        element={
          <ProtectedRoute requiredPermission="REQUEST_VIEW">
            <MainLayout>
              <PipelineDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Purchase Requests */}
      <Route
        path={ROUTES.REQUESTS.LIST}
        element={
          <ProtectedRoute requiredPermission="REQUEST_VIEW">
            <MainLayout>
              <RequestsListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/drafts"
        element={<Navigate to={`${ROUTES.REQUESTS.LIST}?tab=Draft`} replace />}
      />
      <Route
        path={ROUTES.REQUESTS.NEW}
        element={
          <ProtectedRoute requiredPermission="REQUEST_CREATE">
            <MainLayout>
              <NewRequestPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.REQUESTS.NEW_SERVICE}
        element={
          <ProtectedRoute requiredPermission="REQUEST_CREATE">
            <MainLayout>
              <NewServiceRequestPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.REQUESTS.DETAILS(':id')}
        element={
          <ProtectedRoute requiredPermission="REQUEST_VIEW">
            <MainLayout>
              <RequestDetailsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.REQUESTS.EDIT(':id')}
        element={
          <ProtectedRoute requiredPermission="REQUEST_CREATE">
            <MainLayout>
              <EditRequestPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Approvals */}
      <Route
        path={ROUTES.APPROVALS.LIST}
        element={
          <ProtectedRoute requiredPermission="REQUEST_APPROVE">
            <MainLayout>
              <ApprovalsListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.APPROVALS.DETAILS(':approvalId')}
        element={
          <ProtectedRoute requiredPermission="REQUEST_APPROVE">
            <MainLayout>
              <ApprovalDetailsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* NFA / LFA */}
      <Route
        path={ROUTES.NFA.LIST}
        element={
          <ProtectedRoute requiredPermission="NFA_VIEW">
            <MainLayout>
              <NfaListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.NFA.DETAILS(':id')}
        element={
          <ProtectedRoute requiredPermission="NFA_VIEW">
            <MainLayout>
              <NfaDetailsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.NFA.EDIT(':id')}
        element={
          <ProtectedRoute requiredPermission="NFA_VIEW">
            <MainLayout>
              <NfaEditPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.LFA.LIST}
        element={
          <ProtectedRoute requiredPermission="LFA_VIEW">
            <MainLayout>
              <LfaListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.LFA.DETAILS(':id')}
        element={
          <ProtectedRoute requiredPermission="LFA_VIEW">
            <MainLayout>
              <LfaDetailsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.LFA.EDIT(':id')}
        element={
          <ProtectedRoute requiredPermission="LFA_VIEW">
            <MainLayout>
              <LfaEditPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Purchase Requisitions */}
      <Route
        path={ROUTES.PR.LIST}
        element={
          <ProtectedRoute requiredPermission="PR_VIEW">
            <MainLayout>
              <PrListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/pr" element={<Navigate to={ROUTES.PR.LIST} replace />} />
      <Route
        path={ROUTES.PR.NEW}
        element={
          <ProtectedRoute requiredPermission="PR_CREATE">
            <MainLayout>
              <PrCreatePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/pr/new" element={<Navigate to={ROUTES.PR.NEW} replace />} />
      <Route
        path={ROUTES.PR.DETAILS(':id')}
        element={
          <ProtectedRoute requiredPermission="PR_VIEW">
            <MainLayout>
              <PrDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/pr/:id"
        element={
          <ProtectedRoute requiredPermission="PR_VIEW">
            <MainLayout>
              <PrDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* RFQ & Strategic Sourcing */}
      <Route
        path={ROUTES.RFQ.LIST}
        element={
          <ProtectedRoute requiredPermission="RFQ_VIEW">
            <MainLayout>
              <RfqListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/rfq" element={<Navigate to={ROUTES.RFQ.LIST} replace />} />
      <Route
        path={ROUTES.RFQ.NEW}
        element={
          <ProtectedRoute requiredPermission="RFQ_CREATE">
            <MainLayout>
              <RfqCreatePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/rfq/new" element={<Navigate to={ROUTES.RFQ.NEW} replace />} />
      <Route
        path={ROUTES.RFQ.DETAILS(':id')}
        element={
          <ProtectedRoute requiredPermission="RFQ_VIEW">
            <MainLayout>
              <RfqDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/rfq/:id"
        element={
          <ProtectedRoute requiredPermission="RFQ_VIEW">
            <MainLayout>
              <RfqDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.RFQ.EDIT(':id')}
        element={
          <ProtectedRoute requiredPermission="RFQ_VIEW">
            <MainLayout>
              <RfqEditPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Vendors */}
      <Route
        path={ROUTES.VENDORS.LIST}
        element={
          <ProtectedRoute requiredPermission="VENDOR_VIEW">
            <MainLayout>
              <VendorsListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/vendors" element={<Navigate to={ROUTES.VENDORS.LIST} replace />} />
      <Route
        path={ROUTES.VENDORS.NEW}
        element={
          <ProtectedRoute requiredPermission="VENDOR_VIEW">
            <MainLayout>
              <VendorCreatePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/vendors/new" element={<Navigate to={ROUTES.VENDORS.NEW} replace />} />
      <Route
        path={ROUTES.VENDORS.DETAILS(':id')}
        element={
          <ProtectedRoute requiredPermission="VENDOR_VIEW">
            <MainLayout>
              <VendorDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/vendors/:id"
        element={
          <ProtectedRoute requiredPermission="VENDOR_VIEW">
            <MainLayout>
              <VendorDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.VENDORS.EDIT(':id')}
        element={
          <ProtectedRoute requiredPermission="VENDOR_VIEW">
            <MainLayout>
              <VendorEditPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Quotations */}
      <Route
        path={ROUTES.QUOTATIONS.LIST}
        element={
          <ProtectedRoute requiredPermission="QUOTATION_VIEW">
            <MainLayout>
              <QuotationsListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/quotations" element={<Navigate to={ROUTES.QUOTATIONS.LIST} replace />} />
      <Route
        path={ROUTES.QUOTATIONS.COMPARISON(':rfqId')}
        element={
          <ProtectedRoute requiredPermission="QUOTATION_VIEW">
            <MainLayout>
              <QuotationComparisonPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/quotations/comparison/:rfqId"
        element={
          <ProtectedRoute requiredPermission="QUOTATION_VIEW">
            <MainLayout>
              <QuotationComparisonPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Purchase Orders */}
      <Route
        path={ROUTES.PURCHASE_ORDERS.LIST}
        element={
          <ProtectedRoute requiredPermission="PO_VIEW">
            <MainLayout>
              <PurchaseOrdersListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/purchase-orders" element={<Navigate to={ROUTES.PURCHASE_ORDERS.LIST} replace />} />
      <Route
        path={ROUTES.PURCHASE_ORDERS.NEW}
        element={
          <ProtectedRoute requiredPermission="PO_CREATE">
            <MainLayout>
              <PurchaseOrderCreatePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/purchase-orders/new" element={<Navigate to={ROUTES.PURCHASE_ORDERS.NEW} replace />} />
      <Route
        path={ROUTES.PURCHASE_ORDERS.DETAILS(':id')}
        element={
          <ProtectedRoute requiredPermission="PO_VIEW">
            <MainLayout>
              <PurchaseOrderDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/purchase-orders/:id"
        element={
          <ProtectedRoute requiredPermission="PO_VIEW">
            <MainLayout>
              <PurchaseOrderDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PURCHASE_ORDERS.EDIT(':id')}
        element={
          <ProtectedRoute requiredPermission="PO_CREATE">
            <MainLayout>
              <PurchaseOrderEditPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Delivery Tracking */}
      <Route
        path={ROUTES.DELIVERIES.TRACKING}
        element={
          <ProtectedRoute requiredPermission="GRN_VIEW">
            <MainLayout>
              <DeliveryTrackingPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/deliveries" element={<Navigate to={ROUTES.DELIVERIES.TRACKING} replace />} />

      {/* Goods Receipts (GRN) */}
      <Route
        path={ROUTES.GRN.LIST}
        element={
          <ProtectedRoute requiredPermission="GRN_VIEW">
            <MainLayout>
              <GrnListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/grn" element={<Navigate to={ROUTES.GRN.LIST} replace />} />
      <Route
        path={ROUTES.GRN.NEW}
        element={
          <ProtectedRoute requiredPermission="GRN_CREATE">
            <MainLayout>
              <GrnCreatePage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/grn/new" element={<Navigate to={ROUTES.GRN.NEW} replace />} />
      <Route
        path={ROUTES.GRN.DETAILS(':id')}
        element={
          <ProtectedRoute requiredPermission="GRN_VIEW">
            <MainLayout>
              <GrnDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/grn/:id"
        element={
          <ProtectedRoute requiredPermission="GRN_VIEW">
            <MainLayout>
              <GrnDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.GRN.EDIT(':id')}
        element={
          <ProtectedRoute requiredPermission="GRN_CREATE">
            <MainLayout>
              <GrnEditPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Invoices */}
      <Route
        path={ROUTES.INVOICES.LIST}
        element={
          <ProtectedRoute requiredPermission="INVOICE_VIEW">
            <MainLayout>
              <InvoicesListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/invoices" element={<Navigate to={ROUTES.INVOICES.LIST} replace />} />
      <Route
        path={ROUTES.INVOICES.DETAILS(':id')}
        element={
          <ProtectedRoute requiredPermission="INVOICE_VIEW">
            <MainLayout>
              <InvoiceDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/invoices/:id"
        element={
          <ProtectedRoute requiredPermission="INVOICE_VIEW">
            <MainLayout>
              <InvoiceDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.INVOICES.EDIT(':id')}
        element={
          <ProtectedRoute requiredPermission="INVOICE_MATCH">
            <MainLayout>
              <InvoiceEditPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Payments */}
      <Route
        path={ROUTES.PAYMENTS.LIST}
        element={
          <ProtectedRoute requiredPermission="PAYMENT_VIEW">
            <MainLayout>
              <PaymentsListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/procurement/payments" element={<Navigate to={ROUTES.PAYMENTS.LIST} replace />} />
      <Route
        path={ROUTES.PAYMENTS.DETAILS(':id')}
        element={
          <ProtectedRoute requiredPermission="PAYMENT_VIEW">
            <MainLayout>
              <PaymentDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/procurement/payments/:id"
        element={
          <ProtectedRoute requiredPermission="PAYMENT_VIEW">
            <MainLayout>
              <PaymentDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.PAYMENTS.EDIT(':id')}
        element={
          <ProtectedRoute requiredPermission="PAYMENT_PROCESS">
            <MainLayout>
              <PaymentEditPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Analytics & AI */}
      <Route
        path={ROUTES.ANALYTICS}
        element={
          <ProtectedRoute requiredPermission="ANALYTICS_VIEW">
            <MainLayout>
              <AnalyticsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.AI}
        element={
          <ProtectedRoute requiredPermission="AI_RECOMMENDATIONS">
            <MainLayout>
              <AiPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Audit & Logs */}
      <Route
        path={ROUTES.AUDIT_LOGS}
        element={
          <ProtectedRoute requiredPermission="AUDIT_VIEW">
            <MainLayout>
              <AuditListPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path={ROUTES.INTEGRATION_LOGS}
        element={
          <ProtectedRoute requiredPermission="INTEGRATION_LOGS_VIEW">
            <MainLayout>
              <IntegrationLogsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin Portal */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredPermission="USER_MANAGE" requiredPortal="ADMIN">
            <MainLayout>
              <AdminUsersPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      {/* SAP Simulator Portal */}
      <Route path="/sap" element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapDashboardPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.DASHBOARD} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapDashboardPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.MATERIALS} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapMaterialsPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.VENDORS} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapVendorsPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.MASTERS} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapMastersPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.PR} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapPrPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.PO} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapPoPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.GRN} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapGrnPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.INVOICES} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapInvoicesPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.PAYMENTS} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapPaymentsPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.LOGS} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapLogsPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.RETRY_QUEUE} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapLogsPage /></ProtectedRoute>} />
      <Route path={ROUTES.SAP.CONTROL} element={<ProtectedRoute requiredPermission="PR_VIEW" requiredPortal="SAP_SIMULATOR"><SapSimulationControlPage /></ProtectedRoute>} />

      {/* Error & Fallback Routes */}
      <Route
        path="/403"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ForbiddenPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/404"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotFoundPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <NotFoundPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};
